// src/pages/AdminProducts.js
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Filter } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { API_BASE, getImageUrl, isAdminRole, isSuperAdminRole } from '../utils/helpers';
import { useNotify } from '../components/NotificationProvider';
import {
  obtenerProductos,
  agregarProducto,
  editarProducto,
  cambiarDisponibilidadProducto,
  eliminarProducto,
} from '../api/productos';
import { obtenerCategorias } from '../api/categorias';
import { obtenerManicuristas } from '../api/manicuristas';
import { useNavigate } from 'react-router-dom';

const API_UPLOAD_URL = `${API_BASE}/api/upload`;

const DEFAULT_CATEGORIES = [
  { key: 'all', label: 'Todos' },
  { key: 'manicure', label: 'Manicure' },
  { key: 'extension', label: 'Extensiones' },
  { key: 'art', label: 'Nail Art' },
  { key: 'pedicure', label: 'Pedicure' },
];

const buildCategoryOptions = (rows) => {
  const options = (rows || [])
    .map((cat) => String(cat.Nombre || '').trim().toLowerCase())
    .filter(Boolean)
    .map((name) => ({
      key: name,
      label: name.charAt(0).toUpperCase() + name.slice(1)
    }));

  return [{ key: 'all', label: 'Todos' }, ...options];
};

const AdminProducts = ({ user, onLoginClick }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const { success: notifySuccess, error: notifyError, info: notifyInfo } = useNotify();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // evita doble envío/creación
  const [togglingId, setTogglingId] = useState(null); // evita doble toggle

  // Para previsualizar nueva imagen seleccionada (blob:)
  const [imagePreview, setImagePreview] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    category: 'manicure',
    image: '',   // string (URL) o File
    available: true,
    manicuristaId: '',
  });

  const isSuperAdmin = isSuperAdminRole(user?.role);
  const [manicuristas, setManicuristas] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await obtenerProductos(user);
        setProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        notifyError(err.message || 'Error al cargar productos', { duration: 4500 });
        setProducts([]);
      }
      try {
        const cats = await obtenerCategorias();
        setCategories(buildCategoryOptions(cats));
      } catch (err) {
        console.warn('No se pudieron cargar categorias desde BD:', err.message);
      }
      if (isSuperAdmin) {
        try {
          const manis = await obtenerManicuristas(user);
          setManicuristas(Array.isArray(manis) ? manis : []);
        } catch (err) {
          console.warn('No se pudieron cargar manicuristas:', err.message);
        }
      }
    };
    if (user?.id) fetchData();
  }, [user?.id, isSuperAdmin]);

  // Limpieza del object URL para evitar fugas de memoria
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  if (!user || !isAdminRole(user.role)) {
    return (
      <div>
        <motion.div
          className="text-center py-20 bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <img
            src="/images/001.gif"
            alt="Mora.Artt Logo"
            className="w-32 h-32 mx-auto mb-6"
          />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Acceso Restringido
          </h2>
          <p className="text-gray-600 mb-6">
            Solo los administradores tienen acceso a esta sección.
          </p>
          <motion.button
            onClick={onLoginClick}
            className="px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Iniciar Sesión
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      (product.Nombre || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (product.Descripcion || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesFilter =
      filter === 'all' ||
      (product.Categoria || '').toLowerCase() === filter;
    return matchesSearch && matchesFilter;
  });

  const handleAddProduct = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      duration: '',
      category: 'manicure',
      image: '',
      // Se fuerza a false: nuevo producto se crea desactivado para revisión
      available: false,
      manicuristaId: '',
    });
    setEditingProduct(null);
    setImagePreview('');
    setShowAddForm(true);
  };

  const handleEditProduct = (product) => {
    setFormData({
      name: product.Nombre || '',
      description: product.Descripcion || '',
      price:
        product.Precio !== undefined && product.Precio !== null
          ? product.Precio.toString()
          : '',
      duration:
        product.Duracion !== undefined && product.Duracion !== null
          ? product.Duracion.toString()
          : '',
      category: product.Categoria || 'manicure',
      image: product.UrlImagen || '', // URL existente
      available: product.Disponible === 1,
      manicuristaId: product.ManicuristaId || '',
    });
    setImagePreview(''); // se muestra preview solo si el usuario elige un archivo nuevo
    setEditingProduct(product);
    setShowAddForm(true);
  };

  // Subida de archivo si el campo image es File; devuelve URL pública del backend
  const uploadImageIfNeeded = async () => {
    if (formData.image && typeof formData.image !== 'string') {
      const fd = new FormData();
      fd.append('image', formData.image);
      const res = await fetch(API_UPLOAD_URL, {
        method: 'POST',
        body: fd, // no establecer Content-Type; el navegador define boundary
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(t || `HTTP ${res.status}`);
      }
      const data = await res.json();
      return data.relativeUrl || data.imageUrl || ''; // ruta portable del backend
    }
    return formData.image || ''; // ya es URL o vacío
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const imageUrl = await uploadImageIfNeeded();

      const productData = {
        Nombre: formData.name,
        Descripcion: formData.description,
        Precio: parseInt(
          String(formData.price || '').replace(/\./g, ''),
          10
        ) || 0,
        Duracion: parseInt(formData.duration, 10) || 0,
        Categoria: formData.category,
        UrlImagen: imageUrl,
        // Si es creación, siempre desactivado (0). En edición se respeta el valor actual.
        Disponible: editingProduct ? (formData.available ? 1 : 0) : 0,
        ManicuristaId: formData.manicuristaId || null,
      };

      if (editingProduct) {
        await editarProducto(editingProduct.Id, productData, user);
        // Optimistic update sin refetch completo
        setProducts(prev => prev.map(p => p.Id === editingProduct.Id ? { ...p, ...productData, Disponible: productData.Disponible } : p));
        notifySuccess('Producto actualizado', { duration: 3000 });
      } else {
        // Para creación necesitamos Id; mantenemos refetch
        await agregarProducto(productData, user);
        const data = await obtenerProductos(user);
        setProducts(Array.isArray(data) ? data : []);
        notifySuccess('Producto agregado', { duration: 3000 });
      }
      setShowAddForm(false);
      setImagePreview('');
    } catch (error) {
      notifyError('Error al guardar producto: ' + error.message, { duration: 4500 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleAvailability = async (productId, disponibleActual) => {
    try {
      if (togglingId) return;
      setTogglingId(productId);
      const nuevoValor = disponibleActual ? 0 : 1;
      await cambiarDisponibilidadProducto(productId, nuevoValor, user);
      // Optimistic update sin refetch
      setProducts(prev => prev.map(p => p.Id === productId ? { ...p, Disponible: nuevoValor } : p));
      notifyInfo('Disponibilidad actualizada', { duration: 2500 });
    } catch (error) {
      notifyError('Error al cambiar disponibilidad: ' + error.message, { duration: 4500 });
    } finally {
      setTogglingId(null);
    }
  };

  const [pendingDeleteProduct, setPendingDeleteProduct] = useState(null);

  const handleDeleteProduct = (productId) => {
    setPendingDeleteProduct(productId);
  };

  const confirmDeleteProduct = async () => {
    if (!pendingDeleteProduct) return;
    try {
      await eliminarProducto(pendingDeleteProduct, user);
      // Optimistic update sin refetch
      setProducts(prev => prev.filter(p => p.Id !== pendingDeleteProduct));
      notifySuccess('Producto eliminado', { duration: 3000 });
    } catch (error) {
      // Mensaje claro cuando el backend impide eliminar por reservas relacionadas
      const raw = error.message || '';
      const parsed = (() => { try { return JSON.parse(raw); } catch { return null; } })();
      const msg = parsed?.error || raw;
      if (/utilizado\s+en\s+reservas|ligado\s+a\s+reservas|REFERENCE\s+constraint/i.test(msg)) {
        notifyError('No se puede eliminar: el producto está siendo utilizado por reservas. Cancela/actualiza esas reservas o marca el producto como no disponible.', { duration: 5500 });
      } else {
        notifyError('Error al eliminar producto: ' + msg, { duration: 4500 });
      }
    } finally {
      setPendingDeleteProduct(null);
    }
  };

  const cancelDeleteProduct = () => setPendingDeleteProduct(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  function formatPriceInput(value) {
    const numeric = String(value).replace(/\D/g, '');
    return numeric.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  // UI: formulario de alta/edición
  if (showAddForm) {
    return (
      <div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingProduct ? 'Editar Producto' : 'Agregar Nuevo Producto'}
            </h2>

            <form onSubmit={handleSubmitProduct} className="space-y-6">
              {!editingProduct && (
                <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 p-4 rounded-xl">
                  <p className="text-sm leading-relaxed">
                    Este servicio se creará <strong>DESACTIVADO</strong> para revisión interna. Debes
                    activarlo manualmente desde la lista (botón <em>Activar</em>) para que los clientes puedan
                    verlo y reservarlo.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Nombre del Servicio
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      handleInputChange('name', e.target.value)
                    }
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Categoría
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      handleInputChange('category', e.target.value)
                    }
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
                  >
                    {categories.slice(1).map((cat) => (
                      <option key={cat.key} value={cat.key}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {isSuperAdmin && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Manicurista
                    </label>
                    <select
                      value={formData.manicuristaId}
                      onChange={(e) =>
                        handleInputChange('manicuristaId', e.target.value)
                      }
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
                    >
                      <option value="">Sin asignar</option>
                      {manicuristas.map((m) => (
                        <option key={m.Id} value={m.Id}>
                          {m.Nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Precio (CLP)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.price}
                    onChange={(e) => {
                      const formatted = formatPriceInput(e.target.value);
                      setFormData((prev) => ({ ...prev, price: formatted }));
                    }}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Duración (minutos)
                  </label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) =>
                      handleInputChange('duration', e.target.value)
                    }
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Imagen
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setFormData((prev) => ({ ...prev, image: file }));
                      const blobUrl = URL.createObjectURL(file);
                      setImagePreview((prev) => {
                        if (prev && prev.startsWith('blob:')) {
                          URL.revokeObjectURL(prev);
                        }
                        return blobUrl;
                      });
                    } else {
                      setFormData((prev) => ({ ...prev, image: '' }));
                      if (imagePreview && imagePreview.startsWith('blob:')) {
                        URL.revokeObjectURL(imagePreview);
                      }
                      setImagePreview('');
                    }
                  }}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
                />
                {(imagePreview ||
                  (typeof formData.image === 'string' && formData.image)) && (
                  <img
                    src={imagePreview || getImageUrl(formData.image)}
                    alt="Vista previa"
                    className="mt-3 w-32 h-32 object-cover rounded-xl border"
                  />
                )}
              </div>

              {/* Eliminado checkbox de disponibilidad: nuevo producto siempre inicia desactivado */}

              <div className="flex gap-4">
                <motion.button
                  type="submit"
                  className="flex-1 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {editingProduct ? 'Actualizar Producto' : 'Agregar Producto'}
                </motion.button>

                <motion.button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    if (imagePreview && imagePreview.startsWith('blob:')) {
                      URL.revokeObjectURL(imagePreview);
                    }
                    setImagePreview('');
                  }}
                  className="px-6 py-3 bg-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-300 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancelar
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // UI: listado
  return (
    <div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gestión de Productos
            </h1>
            <p className="text-gray-600">Administra tu catálogo de servicios</p>
          </div>

          <motion.button
            onClick={handleAddProduct}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="w-5 h-5" />
            Agregar Producto
          </motion.button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(240px,360px)_1fr] gap-4 mb-8 items-start">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar productos..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
            />
          </div>

          <div className="flex flex-wrap gap-2 min-w-0 max-h-40 overflow-y-auto pr-1">
            {categories.map((category) => (
              <motion.button
                key={category.key}
                title={category.label}
                onClick={() => setFilter(category.key)}
                className={`min-w-0 max-w-full sm:max-w-[220px] flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                  filter === category.key
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                    : 'bg-white/80 text-gray-700 hover:bg-white'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Filter className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{category.label}</span>
              </motion.button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product, index) => (
            <ProductCard
              key={product.Id}
              product={product}
              onEdit={handleEditProduct}
              onToggleAvailability={handleToggleAvailability}
              isAdmin={true}
              onDelete={handleDeleteProduct}
              index={index}
              disableToggle={togglingId === product.Id}
            />
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No se encontraron productos
            </h3>
            <p className="text-gray-500">
              Intenta con otros términos de búsqueda o agrega un nuevo producto.
            </p>
          </motion.div>
        )}
        {pendingDeleteProduct && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
              <h3 className="text-lg font-semibold mb-2">Confirmar eliminación</h3>
              <p className="text-sm text-gray-600 mb-4">¿Seguro que deseas eliminar este producto? Esta acción no se puede deshacer.</p>
              <div className="flex justify-end gap-3">
                <button onClick={cancelDeleteProduct} className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300">Cancelar</button>
                <button onClick={confirmDeleteProduct} className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700">Eliminar</button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminProducts;
